import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DoctorService from "../services/DoctorService";
import AuthService from "../services/AuthService";

export default function DoctorForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doctor, setDoctor] = useState({
    firstName: "",
    lastName: "",
    email: "",
    username: "",
    password: "",
    doctorID: null,
    role: "ROLE_DOCTOR", // default vrednost
  });
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const currentUser = AuthService.getCurrentUser();
  const isAdmin = currentUser?.roles?.includes("ADMIN");
  const isDoctor = currentUser?.roles?.includes("DOCTOR");

  useEffect(() => {
    const loadDoctor = async () => {
      if (id) {
        try {
          const docData = await DoctorService.getDoctorById(id);
          const userData = await DoctorService.getUserByDoctorId(id);
          const doctorID = docData.doctorID || docData.id || null;
          setDoctor({
            ...docData,
            doctorID,
            role: userData?.roles?.[0] || "ROLE_DOCTOR",
          });
          setUser(userData);
        } catch (err) {
          console.error(err);
          setError("Neuspešno učitavanje doktora ili korisnika");
        }
      }
      setLoading(false);
    };
    loadDoctor();
  }, [id]);

  const isSelf =
    currentUser?.username &&
    ((user?.username && currentUser.username === user.username) ||
      currentUser.username === doctor.username);

  const canEdit = isAdmin || isSelf;

  const handleSave = async () => {
    if (!canEdit) {
      alert("Nemate dozvolu da menjate podatke ovog doktora");
      return;
    }

    try {
      setError("");

      const doctorPayload = {
        firstName: doctor.firstName,
        lastName: doctor.lastName,
        email: doctor.email,
      };

      let doctorId;
      if (id) {
        await DoctorService.update(id, doctorPayload);
        doctorId = id;
      } else {
        const created = await DoctorService.create(doctorPayload);
        doctorId = created.doctorID || created.id;
      }

      const userPayload = {};
      if (doctor.username?.trim() || user?.username) {
        userPayload.username = doctor.username?.trim() || user.username;
      }
      if (doctor.password?.trim()) {
        userPayload.password = doctor.password.trim();
      }
      userPayload.doctorId = doctorId;
      userPayload.roles = [doctor.role];

      if (id && user?.userID) {
        await DoctorService.updateUser(user.userID, userPayload);
      } else {
        await DoctorService.createUser(userPayload);
      }

      alert("Doktor uspešno sačuvan!");
      navigate("/doctors");
    } catch (err) {
      console.error("Greška:", err);
      if (err.response && err.response.status === 400) {
        setError(err.response.data || "Username već postoji u bazi");
      } else {
        setError("Greška pri čuvanju doktora");
      }
    }
  };

  if (loading) return <p>Učitavanje...</p>;
  if (error && !doctor.username) return <p style={{ color: "red" }}>{error}</p>;

  return (
    <div
      style={{
        maxWidth: "500px",
        margin: "20px auto",
        padding: "20px",
        border: "1px solid #ddd",
        borderRadius: "8px",
        backgroundColor: "#fefefe",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      }}
    >
      <h2 style={{ textAlign: "center", marginBottom: "20px" }}>
        {id ? "Izmeni doktora" : "Novi doktor"}
      </h2>

      {/* Ime */}
      <div style={{ marginBottom: "15px" }}>
        <label>Ime:</label>
        <input
          type="text"
          value={doctor.firstName}
          onChange={(e) => setDoctor({ ...doctor, firstName: e.target.value })}
          disabled={!canEdit}
          placeholder="Unesite ime"
          style={{
            width: "100%",
            padding: "8px",
            borderRadius: "5px",
            border: "1px solid #ccc",
          }}
        />
      </div>

      {/* Prezime */}
      <div style={{ marginBottom: "15px" }}>
        <label>Prezime:</label>
        <input
          type="text"
          value={doctor.lastName}
          onChange={(e) => setDoctor({ ...doctor, lastName: e.target.value })}
          disabled={!canEdit}
          placeholder="Unesite prezime"
          style={{
            width: "100%",
            padding: "8px",
            borderRadius: "5px",
            border: "1px solid #ccc",
          }}
        />
      </div>

      {/* Email */}
      <div style={{ marginBottom: "15px" }}>
        <label>Email:</label>
        <input
          type="email"
          value={doctor.email}
          onChange={(e) => setDoctor({ ...doctor, email: e.target.value })}
          disabled={!canEdit}
          placeholder="Unesite email"
          style={{
            width: "100%",
            padding: "8px",
            borderRadius: "5px",
            border: "1px solid #ccc",
          }}
        />
      </div>

      {/* Username */}
      <div style={{ marginBottom: "15px" }}>
        <label>Korisničko ime:</label>
        <input
          type="text"
          value={doctor.username || (user?.username || "")}
          onChange={(e) => setDoctor({ ...doctor, username: e.target.value })}
          disabled={!canEdit}
          placeholder="Unesite korisničko ime"
          style={{
            width: "100%",
            padding: "8px",
            borderRadius: "5px",
            border: error ? "1px solid red" : "1px solid #ccc",
          }}
        />
        {error && (
          <p style={{ color: "red", marginTop: "5px", fontSize: "0.9em" }}>
            {error}
          </p>
        )}
      </div>

      {/* Lozinka */}
      <div style={{ marginBottom: "15px" }}>
        <label>Lozinka {id ? "(ostavi prazno ako ne menjaš)" : ""}:</label>
        <input
          type="password"
          value={doctor.password || ""}
          onChange={(e) => setDoctor({ ...doctor, password: e.target.value })}
          disabled={!canEdit}
          placeholder="Unesite lozinku"
          style={{
            width: "100%",
            padding: "8px",
            borderRadius: "5px",
            border: "1px solid #ccc",
          }}
        />
      </div>

      {/* Rola */}
      <div style={{ marginBottom: "15px" }}>
        <label>Uloga:</label>
        <select
          value={doctor.role}
          onChange={(e) => setDoctor({ ...doctor, role: e.target.value })}
          disabled={isDoctor} // 🚫 zabrana menjanja uloge za doktora
          title={isDoctor ? "Doktor ne može menjati uloge" : ""}
          style={{
            width: "100%",
            padding: "8px",
            borderRadius: "5px",
            border: "1px solid #ccc",
            backgroundColor: isDoctor ? "#e9ecef" : "white",
            cursor: isDoctor ? "not-allowed" : "pointer",
          }}
        >
          <option value="ROLE_DOCTOR">Doktor</option>
          <option value="ROLE_ADMIN">Admin</option>
        </select>
      </div>

      {/* Dugmad */}
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <button
          onClick={handleSave}
          disabled={!canEdit}
          style={{
            padding: "10px 20px",
            borderRadius: "5px",
            border: "none",
            backgroundColor: "#28a745",
            color: "#fff",
            cursor: !canEdit ? "not-allowed" : "pointer",
            fontWeight: "bold",
            flex: 1,
          }}
        >
          Sačuvaj
        </button>

        <button
          onClick={() => navigate("/doctors")}
          style={{
            marginLeft: "10px",
            padding: "10px 20px",
            borderRadius: "5px",
            border: "none",
            backgroundColor: "#6c757d",
            color: "#fff",
            cursor: "pointer",
            flex: 1,
          }}
        >
          Nazad
        </button>
      </div>
    </div>
  );
}
